import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import multiplayerAPI from '../services/multiplayerAPI';
import api from '../services/api';
import { platformAPI } from '../services/api';
import '../styles/Challenges.css';
import '../styles/ChallengeModals.css';

const MultiplayerLobby = ({ user, socket }) => {
  const navigate = useNavigate();
  const [maxPlayers, setMaxPlayers] = useState(2);
  const [wager, setWager] = useState(10);
  const [minimumWager, setMinimumWager] = useState(10);
  const [chargePercent, setChargePercent] = useState(7);
  const [availableChallenges, setAvailableChallenges] = useState([]);
  const [myActiveChallenge, setMyActiveChallenge] = useState(null);
  const [myActiveChallenges, setMyActiveChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFriendlyModal, setShowFriendlyModal] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});
  const [invitedFriendId, setInvitedFriendId] = useState('');
  const [friendSearch, setFriendSearch] = useState('');
  const [friendPickerOpen, setFriendPickerOpen] = useState(false);

  const autoRefreshSeconds = 20;


  // Fetch available challenges
  useEffect(() => {
    fetchAvailableChallenges();
    fetchMyActiveChallenge();
    fetchMyActiveChallenges();
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        const nextCharge = Number(res?.data?.chargePercent);

        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setWager((prev) => (Number(prev) > 0 ? Math.max(nextMin, Number(prev) || 0) : nextMin));
        }

        if (Number.isFinite(nextCharge) && nextCharge >= 0) {
          setChargePercent(nextCharge);
        }
      } catch (error) {
        console.error('Failed to load platform settings:', error);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const refreshLobby = () => {
      fetchAvailableChallenges({ silent: true });
      fetchMyActiveChallenge();
      fetchMyActiveChallenges();
    };

    const onPlayerJoined = (data) => {
      if (data?.challengeId) refreshLobby();
    };

    const onGameCancelled = (data) => {
      if (data?.challengeId) refreshLobby();
    };

    const onGameStarted = (data) => {
      if (data?.challengeId) refreshLobby();
    };

    const onGameComplete = (data) => {
      if (data?.challengeId) refreshLobby();
    };

    const onReadyUpdate = (data) => {
      if (data?.challengeId) refreshLobby();
    };

    socket.on('multiplayer:player-joined', onPlayerJoined);
    socket.on('multiplayer:game-cancelled', onGameCancelled);
    socket.on('multiplayer:game-started', onGameStarted);
    socket.on('multiplayer:game-complete', onGameComplete);
    socket.on('multiplayer:ready-update', onReadyUpdate);

    return () => {
      socket.off('multiplayer:player-joined', onPlayerJoined);
      socket.off('multiplayer:game-cancelled', onGameCancelled);
      socket.off('multiplayer:game-started', onGameStarted);
      socket.off('multiplayer:game-complete', onGameComplete);
      socket.off('multiplayer:ready-update', onReadyUpdate);
    };
  }, [socket]);

  useEffect(() => {
    if (showCreateModal || showFriendlyModal) {
      setFriendSearch('');
      setFriendPickerOpen(false);
    }
  }, [showCreateModal, showFriendlyModal]);

  useEffect(() => {
    if (!user?.id) return;
    let timerId;
    const loadFriends = async () => {
      try {
        const res = await api.get('/friends');
        setFriends(res.data || []);
      } catch (error) {
        console.error('Failed to load friends:', error);
      }
    };
    const loadOnlineStatus = async () => {
      try {
        const res = await api.get('/friends/online-status');
        const statusMap = {};
        (res.data || []).forEach((friend) => {
          statusMap[friend.friend_id] = Boolean(friend.is_online);
        });
        setFriendsOnlineStatus(statusMap);
      } catch (error) {
        console.error('Failed to load friend online status:', error);
      }
    };
    loadFriends();
    loadOnlineStatus();
    timerId = window.setInterval(loadOnlineStatus, 5000);
    return () => window.clearInterval(timerId);
  }, [user?.id]);

  useEffect(() => {
    if (!autoRefreshSeconds || autoRefreshSeconds <= 0) return;

    const id = setInterval(() => {
      if (loading) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchAvailableChallenges({ silent: true });
      fetchMyActiveChallenge();
      fetchMyActiveChallenges();
    }, autoRefreshSeconds * 1000);

    return () => clearInterval(id);
  }, [autoRefreshSeconds, loading]);

  const fetchAvailableChallenges = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await multiplayerAPI.getAvailableChallenges({ game: 'lucky' });
      setAvailableChallenges(response.data.challenges || []);
    } catch (error) {
      setMessage(`Error fetching challenges: ${error.response?.data?.error || error.message}`);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchMyActiveChallenge = async () => {
    try {
      const response = await multiplayerAPI.getActiveChallenge({ game: 'lucky' });
      setMyActiveChallenge(response.data || null);
    } catch (error) {
      setMyActiveChallenge(null);
    }
  };

  const fetchMyActiveChallenges = async () => {
    try {
      const response = await multiplayerAPI.getActiveChallenges({ game: 'lucky' });
      setMyActiveChallenges(response.data || []);
    } catch (error) {
      setMyActiveChallenges([]);
    }
  };

  const handleCreateChallenge = async ({ friendly = false } = {}) => {
    const wagerValue = friendly ? 0 : Math.max(0, Number(wager) || 0);
    const hasInvite = Boolean(invitedFriendId);
    const activeChallengeCount = myCreatedChallenges.length;

    if (activeChallengeCount >= 3) {
      setMessage('You can only have up to 3 active challenges.');
      setShowCreateModal(false);
      setShowFriendlyModal(false);
      return;
    }

    if (hasInvite && Number(maxPlayers) !== 2) {
      setMessage('Direct challenges support 2 players only.');
      setShowCreateModal(false);
      setShowFriendlyModal(false);
      return;
    }

    if (!friendly && (wagerValue <= 0 || wagerValue > (user?.balance ?? 0))) {
      setMessage('Invalid wager amount');
      setShowCreateModal(false);
      setShowFriendlyModal(false);
      return;
    }

    if (!friendly && wagerValue < Number(minimumWager || 0)) {
      setMessage(`Wager must be at least ${minimumWager} wikipenny`);
      setShowCreateModal(false);
      setShowFriendlyModal(false);
      return;
    }

    try {
      setLoading(true);
      const invitedUserId = hasInvite ? Number(invitedFriendId) : null;
      const response = await multiplayerAPI.createChallenge(maxPlayers, wagerValue, {
        friendly,
        game: 'lucky',
        meta: invitedUserId ? { invitedUserId } : undefined
      });
      
      if (response.data.success) {
        const challengeId = response.data.challenge.id;
        setMessage(`✅ Challenge created! Waiting for ${maxPlayers - 1} player(s)...`);
        setWager(Number(minimumWager || 10));
        setMaxPlayers(2);
        setShowCreateModal(false);
        setShowFriendlyModal(false);
        setInvitedFriendId('');

        setMyActiveChallenge(response.data.challenge);
        fetchMyActiveChallenges();

        // Join the challenge via Socket.IO
        socket.emit('multiplayer:join-challenge', {
          challengeId: challengeId,
          userId: user.id
        });

        // Stay on lobby; user can open the room from the created list
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.response?.data?.error || error.message}`);
      setShowCreateModal(false);
      setShowFriendlyModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      setLoading(true);
      const response = await multiplayerAPI.joinChallenge(challengeId);
      
      if (response.data.success) {
        setMessage(`✅ ${response.data.message}`);
        
        // Join the challenge via Socket.IO
        socket.emit('multiplayer:join-challenge', {
          challengeId: challengeId,
          userId: user.id
        });

        // Always send the player to the game lobby to wait for their turn
        setTimeout(() => {
          navigate(`/multiplayer/game/${challengeId}`);
        }, response.data.isFull ? 300 : 800);

        // Refresh available challenges list for others
        fetchAvailableChallenges();
        fetchMyActiveChallenge();
        fetchMyActiveChallenges();
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelChallenge = async (challengeId) => {
    try {
      setLoading(true);
      await multiplayerAPI.cancelChallenge(challengeId);
      setMessage('✅ Challenge cancelled. Wagers refunded.');
      fetchAvailableChallenges();
      fetchMyActiveChallenge();
      fetchMyActiveChallenges();
    } catch (error) {
      setMessage(`❌ Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const challengeSorter = (left, right) => {
    const leftPlayers = Number(left?.current_players || 0);
    const rightPlayers = Number(right?.current_players || 0);
    if (leftPlayers !== rightPlayers) return rightPlayers - leftPlayers;

    const leftCreated = new Date(left?.created_at || 0).getTime();
    const rightCreated = new Date(right?.created_at || 0).getTime();
    return rightCreated - leftCreated;
  };

  const combinedMyChallenges = [...(myActiveChallenges || []), ...(myActiveChallenge ? [myActiveChallenge] : [])];
  const myChallengesById = new Map();
  combinedMyChallenges.forEach((challenge) => {
    if (challenge?.id) {
      myChallengesById.set(challenge.id, challenge);
    }
  });
  const myCreatedChallenges = Array.from(myChallengesById.values()).sort(challengeSorter);

  const openChallenges = (availableChallenges || [])
    .filter((challenge) => Number(challenge.creator_id) !== Number(user?.id))
    .sort(challengeSorter);

  const normalizedSearch = friendSearch.trim().toLowerCase();
  const filteredFriends = normalizedSearch
    ? friends.filter((friend) => {
        const name = String(friend.friend_username || friend.username || '').toLowerCase();
        return name.includes(normalizedSearch);
      })
    : friends;

  const getChallengeMode = (challenge) => (Number(challenge?.wager || 0) > 0 ? 'betting' : 'friendly');

  return (
    <div className="challenges-container">
      <div className="challenges-header">
        <h1>Lucky Number Lobby</h1>
        <div className="challenges-actions">
          <button
            className="btn-create-challenge"
            onClick={() => {
              setInvitedFriendId('');
              setShowCreateModal(true);
            }}
          >
            + Real Challenge
          </button>
          <button
            className="btn-create-challenge btn-create-friendly"
            onClick={() => {
              setInvitedFriendId('');
              setShowFriendlyModal(true);
            }}
          >
            + Friendly Challenge
          </button>
        </div>
      </div>

      {message && (
        <div className="error-message">
          {message}
        </div>
      )}

      <div className="open-challenges">
        <h2>🎲 Your Active Challenge</h2>
        {myCreatedChallenges.length === 0 ? (
          <div className="no-challenges">
            <p>No active challenges yet</p>
            <p>Create or join a challenge to start the lobby.</p>
          </div>
        ) : (
          <div className="challenges-list">
            {myCreatedChallenges.map((challenge) => (
              (() => {
                const mode = getChallengeMode(challenge);
                const isBetting = mode === 'betting';
                return (
              <div key={challenge.id} className="challenge-row">
                <div className="challenge-row-main">
                  <div className="challenge-row-title">
                    {challenge.creator_username}
                    <span className="challenge-id">#{challenge.id}</span>
                    <span className={`challenge-mode ${isBetting ? 'betting' : 'friendly'}`}>
                      {isBetting ? '⚠️ Betting' : '✅ Friendly'}
                    </span>
                  </div>
                  <div className="challenge-row-meta">
                    <span className={`challenge-wager ${isBetting ? 'betting' : 'friendly'}`}>
                      Wager: {challenge.wager} wikipenny
                    </span>
                    <span>Players: {challenge.current_players}/{challenge.max_players}</span>
                  </div>
                </div>
                <div className="challenge-row-actions">
                  <button
                    className="btn-accept"
                    onClick={() => navigate(`/multiplayer/game/${challenge.id}`)}
                  >
                    Play
                  </button>
                  {challenge.game_status === 'waiting' && Number(challenge.current_players || 0) <= 1 && (
                    <button
                      className="btn-reject"
                      onClick={() => handleCancelChallenge(challenge.id)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
                );
              })()
            ))}
          </div>
        )}
      </div>

      <div className="open-challenges">
        <h2>Available Challenges</h2>
        {loading ? (
          <div className="no-challenges">
            <p>Loading challenges...</p>
          </div>
        ) : openChallenges.length === 0 ? (
          <div className="no-challenges">
            <p>No available challenges yet</p>
            <p>Create one to start the lobby.</p>
          </div>
        ) : (
          <div className="challenges-list">
            {openChallenges.map((challenge) => (
              (() => {
                const mode = getChallengeMode(challenge);
                const isBetting = mode === 'betting';
                return (
              <div key={challenge.id} className="challenge-row">
                <div className="challenge-row-main">
                  <div className="challenge-row-title">
                    {challenge.creator_username}
                    <span className="challenge-id">#{challenge.id}</span>
                    <span className={`challenge-mode ${isBetting ? 'betting' : 'friendly'}`}>
                      {isBetting ? '⚠️ Betting' : '✅ Friendly'}
                    </span>
                  </div>
                  <div className="challenge-row-meta">
                    <span className={`challenge-wager ${isBetting ? 'betting' : 'friendly'}`}>
                      Wager: {challenge.wager} wikipenny
                    </span>
                    <span>Players: {challenge.current_players}/{challenge.max_players}</span>
                  </div>
                </div>
                <div className="challenge-row-actions">
                  <button
                    className="btn-accept"
                    onClick={() => handleJoinChallenge(challenge.id)}
                    disabled={loading}
                  >
                    Join
                  </button>
                </div>
              </div>
                );
              })()
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content create-challenge-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Real Challenge</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Number of Players</label>
                <select
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(parseInt(e.target.value, 10))}
                  disabled={loading || Boolean(invitedFriendId)}
                >
                  <option value={2}>2 Players</option>
                  <option value={3}>3 Players</option>
                  <option value={4}>4 Players</option>
                  <option value={5}>5 Players</option>
                </select>
                {invitedFriendId && (
                  <small>Direct challenges are 2-player only.</small>
                )}
              </div>

              <div className="form-group">
                <label>Invite Friend (optional)</label>
                <button
                  type="button"
                  className="friend-dropdown-toggle"
                  onClick={() => setFriendPickerOpen((prev) => !prev)}
                >
                  <span className={`friend-status ${invitedFriendId && friendsOnlineStatus[invitedFriendId] ? 'online' : 'offline'}`}></span>
                  <span className="friend-name">
                    {invitedFriendId
                      ? (friends.find((f) => String(f.friend_id) === String(invitedFriendId))?.friend_username
                        || friends.find((f) => String(f.friend_id) === String(invitedFriendId))?.username
                        || 'Selected friend')
                      : 'Open challenge'}
                  </span>
                  <span className="friend-dropdown-caret">▾</span>
                </button>
                {friendPickerOpen && (
                  <div className="friends-picker">
                    <input
                      className="friend-search"
                      type="text"
                      value={friendSearch}
                      onChange={(e) => setFriendSearch(e.target.value)}
                      placeholder="Search friends..."
                    />
                    <button
                      type="button"
                      className={`friend-option ${invitedFriendId ? '' : 'selected'}`}
                      onClick={() => {
                        setInvitedFriendId('');
                        setFriendPickerOpen(false);
                      }}
                    >
                      <span className="friend-status offline"></span>
                      <span className="friend-name">Open challenge</span>
                    </button>
                    {filteredFriends.map((friend) => (
                      <button
                        key={friend.friendship_id || friend.friend_id}
                        type="button"
                        className={`friend-option ${String(invitedFriendId) === String(friend.friend_id) ? 'selected' : ''}`}
                        onClick={() => {
                          setInvitedFriendId(String(friend.friend_id));
                          setMaxPlayers(2);
                          setFriendPickerOpen(false);
                        }}
                      >
                        <span className={`friend-status ${friendsOnlineStatus[friend.friend_id] ? 'online' : 'offline'}`}></span>
                        <span className="friend-name">{friend.friend_username || friend.username}</span>
                      </button>
                    ))}
                  </div>
                )}
                <small>Leave empty to create an open challenge anyone can join.</small>
              </div>

              <div className="form-group">
                <label>Wager Amount</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    value={wager}
                    onChange={(e) => {
                      const numericValue = Number.parseInt(e.target.value, 10);
                      setWager(Number.isFinite(numericValue) ? numericValue : 0);
                    }}
                    min={minimumWager}
                    max={user?.balance || 0}
                    step="1"
                    disabled={loading}
                  />
                  <span className="unit">wikipenny</span>
                </div>
                <small>Minimum {minimumWager} • Available wikipenny: {user?.balance}</small>
                <small>Tip: You can type any amount; values below minimum won't be accepted.</small>
              </div>

              <div className="summary">
                <div className="summary-row">
                  <span>Total pot:</span>
                  <strong>{(Number(wager) || 0) * (Number(maxPlayers) || 0)} wikipenny</strong>
                </div>
                <div className="summary-row">
                  <span>Platform fee:</span>
                  <strong>{chargePercent}%</strong>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={() => handleCreateChallenge({ friendly: false })}
                disabled={loading}
              >
                Create Real Challenge
              </button>
            </div>
          </div>
        </div>
      )}

      {showFriendlyModal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content create-challenge-modal friendly-challenge-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Friendly Challenge</h2>
              <button className="close-btn" onClick={() => setShowFriendlyModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Number of Players</label>
                <select
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(parseInt(e.target.value, 10))}
                  disabled={loading || Boolean(invitedFriendId)}
                >
                  <option value={2}>2 Players</option>
                  <option value={3}>3 Players</option>
                  <option value={4}>4 Players</option>
                  <option value={5}>5 Players</option>
                </select>
                {invitedFriendId && (
                  <small>Direct challenges are 2-player only.</small>
                )}
              </div>

              <div className="form-group">
                <label>Invite Friend (optional)</label>
                <button
                  type="button"
                  className="friend-dropdown-toggle"
                  onClick={() => setFriendPickerOpen((prev) => !prev)}
                >
                  <span className={`friend-status ${invitedFriendId && friendsOnlineStatus[invitedFriendId] ? 'online' : 'offline'}`}></span>
                  <span className="friend-name">
                    {invitedFriendId
                      ? (friends.find((f) => String(f.friend_id) === String(invitedFriendId))?.friend_username
                        || friends.find((f) => String(f.friend_id) === String(invitedFriendId))?.username
                        || 'Selected friend')
                      : 'Open challenge'}
                  </span>
                  <span className="friend-dropdown-caret">▾</span>
                </button>
                {friendPickerOpen && (
                  <div className="friends-picker">
                    <input
                      className="friend-search"
                      type="text"
                      value={friendSearch}
                      onChange={(e) => setFriendSearch(e.target.value)}
                      placeholder="Search friends..."
                    />
                    <button
                      type="button"
                      className={`friend-option ${invitedFriendId ? '' : 'selected'}`}
                      onClick={() => {
                        setInvitedFriendId('');
                        setFriendPickerOpen(false);
                      }}
                    >
                      <span className="friend-status offline"></span>
                      <span className="friend-name">Open challenge</span>
                    </button>
                    {filteredFriends.map((friend) => (
                      <button
                        key={friend.friendship_id || friend.friend_id}
                        type="button"
                        className={`friend-option ${String(invitedFriendId) === String(friend.friend_id) ? 'selected' : ''}`}
                        onClick={() => {
                          setInvitedFriendId(String(friend.friend_id));
                          setMaxPlayers(2);
                          setFriendPickerOpen(false);
                        }}
                      >
                        <span className={`friend-status ${friendsOnlineStatus[friend.friend_id] ? 'online' : 'offline'}`}></span>
                        <span className="friend-name">{friend.friend_username || friend.username}</span>
                      </button>
                    ))}
                  </div>
                )}
                <small>Leave empty to create an open challenge anyone can join.</small>
              </div>

              <div className="summary">
                <div className="summary-row">
                  <span>Wager:</span>
                  <strong>0 wikipenny</strong>
                </div>
                <div className="summary-row">
                  <span>Total pot:</span>
                  <strong>0 wikipenny</strong>
                </div>
              </div>

              <div className="info-box">
                <p>✓ Friendly challenges have no wager</p>
                <p>✓ Great for practice and quick matches</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowFriendlyModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={() => handleCreateChallenge({ friendly: true })}
                disabled={loading}
              >
                Create Friendly Challenge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerLobby;

